import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Button, 
  Space, 
  Typography, 
  Tag, 
  Collapse, 
  Upload, 
  Divider,
  Input,
} from 'antd';
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  RocketOutlined,
  PlayCircleOutlined,
  DesktopOutlined,
  FolderOpenOutlined,
  ThunderboltOutlined,
  CodeOutlined,
  LoadingOutlined,
  EditOutlined,
  SendOutlined,
  SettingOutlined,
  DatabaseOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { io, Socket } from 'socket.io-client';
import 'xterm/css/xterm.css';

const { Title, Text } = Typography;
const { Panel } = Collapse;

export default function InstanceConnectPage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [searchParams] = useSearchParams();
  
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminal = useRef<Terminal | null>(null);
  const socket = useRef<Socket | null>(null);
  const fitAddon = useRef<FitAddon | null>(null);
  
  const [connected, setConnected] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [customCommand, setCustomCommand] = useState('');

  // Get instance name from URL params
  const instanceName = searchParams.get('name') || 'GPU Instance';

  useEffect(() => {
    if (!terminalRef.current || !instanceId) return;

    // Initialize terminal
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'JetBrains Mono, Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#0d1117',
        foreground: '#c9d1d9',
        cursor: '#58a6ff',
        selectionBackground: '#264f78',
      },
      scrollback: 5000,
    });

    const fit = new FitAddon();
    const webLinks = new WebLinksAddon();

    term.loadAddon(fit);
    term.loadAddon(webLinks);
    term.open(terminalRef.current);
    
    fitAddon.current = fit;
    terminal.current = term;

    // Fit terminal after a brief delay
    setTimeout(() => fit.fit(), 100);

    // Connect WebSocket
    const socketUrl = import.meta.env.VITE_WS_URL || 
      (window.location.protocol === 'https:' 
        ? `wss://${window.location.hostname}:8000`
        : `ws://${window.location.hostname}:8000`);
    
    const sock = io(`${socketUrl}/terminal`, {
      auth: { token: localStorage.getItem('token') },
    });

    socket.current = sock;

    sock.on('connect', () => {
      term.writeln('\x1b[32m🔌 Connecting to instance...\x1b[0m');
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      sock.emit('terminal:connect', {
        instanceId,
        organizationId: user.organizationId,
      });
    });

    sock.on('terminal:connected', () => {
      setConnected(true);
      term.clear();
      term.writeln('\x1b[32m✅ Connected!\x1b[0m');
      term.writeln(`\x1b[36mInstance:\x1b[0m ${instanceName}`);
      term.writeln('\x1b[90m' + '─'.repeat(50) + '\x1b[0m');
    });

    sock.on('terminal:output', (data: string) => term.write(data));
    sock.on('terminal:error', (err: any) => term.writeln(`\x1b[31m❌ ${err.message}\x1b[0m`));
    sock.on('terminal:disconnected', () => {
      term.writeln('\x1b[33m🔌 Disconnected\x1b[0m');
      setConnected(false);
    });
    sock.on('disconnect', () => setConnected(false));

    term.onData((data) => sock.emit('terminal:input', data));

    const handleResize = () => {
      fit.fit();
      sock.emit('terminal:resize', { cols: term.cols, rows: term.rows });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      term.dispose();
      sock.disconnect();
    };
  }, [instanceId, instanceName]);

  const runCommand = (cmd: string) => {
    if (socket.current && connected) {
      socket.current.emit('terminal:input', cmd + '\n');
    }
  };

  const handleDeploy = () => {
    setDeploying(true);
    
    // Create a setup script using heredoc to avoid escaping issues
    const setupScript = `cat << 'EOF' > setup_env.sh
#!/bin/bash
set -e

echo "🚀 Setting up Thiran Environment..."

# Create directories
echo "📂 Creating directory structure..."
mkdir -p ~/thiran-finetuning/{datasets,scripts,models,checkpoints,logs}

# Function to check if a python package is installed
check_installed() {
    python3 -c "import $1" 2>/dev/null
    return $?
}

# Install tools
echo "📦 Checking system tools..."
if ! command -v git &> /dev/null; then
    echo "   - Installing git, htop..."
    apt-get update && apt-get install -y python3-pip git htop
else
    echo "   - System tools already installed."
fi

# Install Python libraries
echo "🐍 Checking Python dependencies..."
PACKAGES="torch transformers datasets peft accelerate bitsandbytes wandb"
MISSING_PACKAGES=""

for pkg in $PACKAGES; do
    if ! check_installed "$pkg"; then
        MISSING_PACKAGES="$MISSING_PACKAGES $pkg"
    fi
done

if [ -n "$MISSING_PACKAGES" ]; then
    echo "   - Installing missing packages: $MISSING_PACKAGES"
    echo "   - This may take a few minutes..."
    pip install $MISSING_PACKAGES
else
    echo "   - All Python libraries already installed! ✅"
fi

echo "✅ Environment Ready!"
echo "📂 Workspace: ~/thiran-finetuning"
ls -F ~/thiran-finetuning/
EOF

bash setup_env.sh
`;
    runCommand(setupScript);
    setTimeout(() => setDeploying(false), 5000);
  };

  const handleCheckGpu = () => {
    runCommand('nvidia-smi');
  };

  const handleStartTraining = () => {
    runCommand('cd ~/thiran-finetuning && python finetune_qwen3.py');
  };

  const handleListFiles = () => {
    runCommand('ls -la ~/thiran-finetuning/');
  };

  const handleDiskSpace = () => {
    runCommand('df -h');
  };

  const handleCustomCommand = () => {
    if (customCommand.trim()) {
      runCommand(customCommand);
      setCustomCommand('');
    }
  };

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: '#0d1117',
    }}>
      {/* Header */}
      <div style={{ 
        padding: '12px 24px', 
        background: '#161b22',
        borderBottom: '1px solid #30363d',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Space>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => window.close()}
            style={{ color: '#8b949e' }}
          >
            Close
          </Button>
          <Divider type="vertical" style={{ borderColor: '#30363d' }} />
          <DesktopOutlined style={{ color: '#58a6ff', fontSize: 18 }} />
          <Title level={4} style={{ margin: 0, color: '#c9d1d9' }}>
            {instanceName}
          </Title>
          <Tag color={connected ? 'green' : 'default'}>
            {connected ? 'Connected' : 'Disconnected'}
          </Tag>
        </Space>
        <Text style={{ color: '#8b949e' }}>ThiranTech AI Platform</Text>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left: Terminal */}
        <div style={{ flex: 7, display: 'flex', flexDirection: 'column' }}>
          <div 
            ref={terminalRef} 
            style={{ 
              flex: 1, 
              height: '100%',
              width: '100%',
              background: '#0d1117',
              padding: 0,
              overflow: 'hidden',
            }} 
          />
        </div>

        {/* Right: Actions Panel - Dark Theme */}
        <div style={{ 
          width: 300, 
          background: '#0d1117', 
          borderLeft: '1px solid #30363d',
          overflowY: 'auto',
          padding: '16px',
          color: '#c9d1d9'
        }}>
          <Collapse 
            defaultActiveKey={['deploy', 'training']} 
            bordered={false}
            ghost
            expandIconPosition="end"
          >
            {/* Dataset Management */}
            <Panel 
              header={<Space><FileOutlined style={{ color: '#58a6ff' }} /> <Text style={{ color: '#c9d1d9' }} strong>Dataset</Text></Space>} 
              key="dataset"
              style={{ padding: 0 }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Upload showUploadList={false} beforeUpload={() => false}>
                  <Button type="dashed" ghost block icon={<CloudUploadOutlined />} style={{ color: '#8b949e', borderColor: '#30363d' }}>
                    Upload
                  </Button>
                </Upload>
                <Button ghost block icon={<FolderOpenOutlined />} onClick={handleListFiles} style={{ color: '#8b949e', borderColor: '#30363d' }}>
                  Files
                </Button>
              </div>
            </Panel>

            <Divider style={{ margin: '12px 0', borderColor: '#21262d' }} />

            {/* Environment Setup */}
            <Panel 
              header={<Space><SettingOutlined style={{ color: '#58a6ff' }} /> <Text style={{ color: '#c9d1d9' }} strong>Environment</Text></Space>} 
              key="deploy"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  icon={deploying ? <LoadingOutlined /> : <RocketOutlined />} 
                  block
                  onClick={handleDeploy}
                  loading={deploying}
                  style={{ marginBottom: 4 }}
                >
                  Deploy Stack
                </Button>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <Button ghost block icon={<ThunderboltOutlined />} onClick={handleCheckGpu} style={{ color: '#8b949e', borderColor: '#30363d' }}>
                    Check GPU
                  </Button>
                  <Button ghost block icon={<DesktopOutlined />} onClick={handleDiskSpace} style={{ color: '#8b949e', borderColor: '#30363d' }}>
                    Disk
                  </Button>
                </div>
              </Space>
            </Panel>

            <Divider style={{ margin: '12px 0', borderColor: '#21262d' }} />

            {/* Training */}
            <Panel 
              header={<Space><EditOutlined style={{ color: '#58a6ff' }} /> <Text style={{ color: '#c9d1d9' }} strong>Training</Text></Space>} 
              key="training"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  icon={<PlayCircleOutlined />} 
                  block
                  onClick={handleStartTraining}
                  style={{ background: '#238636', border: 'none', marginBottom: 8 }}
                >
                  Start Training
                </Button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input 
                    placeholder="Custom command..." 
                    value={customCommand}
                    onChange={(e) => setCustomCommand(e.target.value)}
                    onPressEnter={handleCustomCommand}
                    style={{ background: '#0d1117', border: '1px solid #30363d', color: '#c9d1d9' }}
                  />
                  <Button 
                    type="primary" 
                    icon={<CodeOutlined />} 
                    onClick={handleCustomCommand}
                    style={{ background: '#21262d', borderColor: '#30363d' }}
                  />
                </div>
              </Space>
            </Panel>

            <Divider style={{ margin: '12px 0', borderColor: '#21262d' }} />

            {/* Models */}
            <Panel 
              header={<Space><DatabaseOutlined style={{ color: '#58a6ff' }} /> <Text style={{ color: '#c9d1d9' }} strong>Models</Text></Space>} 
              key="models"
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Button ghost block icon={<FolderOpenOutlined />} onClick={() => runCommand('ls -la ~/thiran-finetuning/models/')} style={{ color: '#8b949e', borderColor: '#30363d' }}>
                  List
                </Button>
                <Button ghost block icon={<CloudUploadOutlined />} style={{ color: '#8b949e', borderColor: '#30363d' }}>
                  Export
                </Button>
              </div>
            </Panel>

            <Divider style={{ margin: '12px 0', borderColor: '#21262d' }} />

            {/* Quick Actions */}
            <Panel 
              header={<Space><SendOutlined style={{ color: '#58a6ff' }} /> <Text style={{ color: '#c9d1d9' }} strong>Quick Actions</Text></Space>} 
              key="quick"
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <Button size="small" ghost onClick={() => runCommand('htop')} style={{ color: '#8b949e', borderColor: '#30363d' }}>htop</Button>
                <Button size="small" ghost onClick={() => runCommand('watch -n 1 nvidia-smi')} style={{ color: '#8b949e', borderColor: '#30363d' }}>GPU</Button>
                <Button size="small" ghost onClick={() => terminal.current?.clear()} style={{ color: '#8b949e', borderColor: '#30363d' }}>Clear</Button>
              </div>
            </Panel>
          </Collapse>
        </div>
      </div>
    </div>
  );
}
